import { createClient } from '@supabase/supabase-js'
import { offline, setOfflineAuth } from './offline'
import { memberPermissions } from './companyMembers'
import { CLIENT_COLS } from './clientCols'
import { withService, technicianConfig } from '../service/state'   // Service package: off = the config unchanged

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Bypass the Web Locks navigator lock that deadlocks under concurrent
    // getSession/getUser calls (the "Lock not released within 5000ms" error).
    lock: (name, acquireTimeout, fn) => fn(),
  },
  // offline mode (src/lib/offline): reads kept per login, the daily jobs queued offline and synced when back online
  global: { fetch: (...a) => offline.fetch(...a) },
})
setOfflineAuth({
  token: async () => { const { data } = await supabase.auth.getSession(); return (data && data.session && data.session.access_token) || null },
  uid: () => { try { for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) { const v = JSON.parse(localStorage.getItem(k)); return (v && v.user && v.user.id) || null } } } catch (e) {} return null },
})

// Get the current logged-in client's config (baserow token, table IDs, webhook)
// Supports both vessel accounts (clients table) and crew sub-accounts (crew_accounts table)
export async function getClientConfig() {
  return withService(await baseClientConfig(), supabase)   // Service package (src/service/state.js)
}
async function baseClientConfig() {
  let { data: { user }, error: userErr } = await supabase.auth.getUser()
  // offline: getUser() needs the network - the stored session's user, and the config from this login's cached reads
  const netErr = userErr && (offline.isOffline() || (typeof navigator !== 'undefined' && !navigator.onLine) || /AuthRetryableFetchError|fetch|network/i.test((userErr.name || '') + ' ' + (userErr.message || '')))
  if (!user && netErr) { const { data: { session } } = await supabase.auth.getSession(); if (session && session.user) user = session.user }
  if (!user) return null

  // Check vessel account first
  const { data: vessel } = await supabase
    .from('clients')
    .select(CLIENT_COLS)   // named columns: the webhook / Baserow token are never sent to the browser (src/lib/clientCols.js)
    .eq('user_id', user.id)
    .maybeSingle()
  if (vessel) return { ...vessel, role: 'captain', accountType: 'vessel' }

  // Check management company account
  const { data: company } = await supabase
    .from('companies')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle()
  if (company) {
    const { data: fleet } = await supabase.from('clients').select(CLIENT_COLS).eq('company_id', company.id).order('vessel_name')
    const vessels = fleet || []
    // A company is NOT a vessel — it lands on the Fleet page. id is the company id so
    // the app has a stable key, but accountType 'company' routes to the fleet view.
    return { id: company.id, accountType: 'company', role: 'company', companyId: company.id, companyName: company.name, vessel_name: company.name, fleet: vessels }
  }

  // A member of a management company (sql/fleet-4.sql): the company's Fleet page with their own vessels (the database shows
  // only those) and their own rights; switched off by the company = locked out (accountType 'locked', App.jsx says so)
  const { data: member, error: memberErr } = await supabase.rpc('hc_my_membership')
  if (!memberErr && member && member.company_id) {
    if (!member.active) return { id: member.id, accountType: 'locked', role: 'locked', companyName: member.company_name, vessel_name: member.company_name }
    const { data: fleet } = await supabase.from('clients').select(CLIENT_COLS).eq('company_id', member.company_id).order('vessel_name')
    return { id: member.company_id, accountType: 'company', role: 'company', companyId: member.company_id, companyName: member.company_name, vessel_name: member.company_name,
      fleet: fleet || [], member, memberLabel: member.label, permissions: memberPermissions(member.rights) }
  }

  // Check crew sub-account
  const { data: crew } = await supabase
    .from('crew_accounts')
    .select('*, clients(' + CLIENT_COLS + '), permissions')
    .eq('user_id', user.id)
    .eq('active', true)
    .maybeSingle()
  if (crew && crew.clients) {
    const vessel = crew.clients
    // crewId: the login's own crew record (crew_accounts.crew_id, sql/test-fixes-5.sql) - its own personal data and documents
    return { ...vessel, role: crew.role, crewName: crew.name, crewId: crew.crew_id || null, accountType: 'crew', crewAccountId: crew.id, permissions: crew.permissions || {} }
  }

  return technicianConfig(user, supabase)   // Service package: a technician login (null for anyone else)
}
